import {
  useViewRemindersWithSuspense,
  type ViewRemindersRequest,
  type ViewRemindersResponse
} from "@swivu/client-api";
import { createContext, useContext } from "react";
import { DateTime } from "luxon";
import { groupBy } from "../utils/groupBy.js";
import { AccordionV2 } from "./AccordionV2.js";
import { Badge } from "./Badge.js";
import { decodeFA } from "../utils/decodeFA.js";
import ErrorBoundary from "./ErrorBoundary.js";
import SuspenseWithDelay from "./SuspenseWithDelay.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";
import { cn } from "../utils/cn.js";

type ViewRemindersVariables = ViewRemindersRequest & {
  timezone: string;
  id: string;
};
type ViewRemindersProps = ViewRemindersVariables & {
  reminders: ViewRemindersResponse;
};

const ViewRemindersContext = createContext<ViewRemindersProps | null>(null);

const ViewRemindersProviderWrapper = ({
  children,
  ...props
}: ViewRemindersVariables & { children: React.ReactNode }) => {
  return (
    <SuspenseWithDelay fallback={<ViewRemindersSkeleton />}>
      <ErrorBoundary>
        <ViewRemindersProvider {...props}>{children}</ViewRemindersProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

const ViewRemindersProvider = ({
  children,
  ...props
}: ViewRemindersVariables & { children: React.ReactNode }) => {
  const { data: reminders } = useViewRemindersWithSuspense({
    variables: {
      ...props
    }
  });
  return (
    <ViewRemindersContext.Provider value={{ ...props, reminders }}>
      {children}
    </ViewRemindersContext.Provider>
  );
};

const useViewReminders = () => {
  const context = useContext(ViewRemindersContext);
  if (!context) {
    throw new Error(
      "useViewReminders must be used within a ViewRemindersProvider"
    );
  }
  return context;
};

const ViewReminders = () => {
  const { reminders, timezone, id } = useViewReminders();
  const isGummy = useIsGummy();
  const groupedReminders = groupBy(reminders ?? [], "status");
  const subItems = [
    { field: "entity_name", icon: "bars-progress" },
    { field: "displayRecurringInterval", icon: "calendar-week" },
    { field: "repeat_count", icon: "repeat" }
  ] as const;

  return (
    <div>
      <AccordionV2.PersistentRoot
        persistKey={`view-reminders-${id}`}
        defaultValue={["undefined"]}
      >
        {Object.entries(groupedReminders).map(([status, statusReminders]) => (
          <AccordionV2.Item key={status} value={status ?? "undefined"}>
            <div className="bg-mono-100 dark:bg-mono-950 sticky top-[0px] z-10 -mx-2 px-2">
              <AccordionV2.Trigger
                className="mobile-pref-view-trigger inline-flex w-full py-0"
                headerClassName=" flex space-x-4 !border-b-0 py-0"
              >
                <h3
                  className={cn(
                    "mobile-pref-section-title dark:border-mono-700/50 flex items-center justify-between gap-4 py-3",
                    isGummy
                      ? "text-2xl font-black tracking-tight text-mono-900 dark:text-white"
                      : "text-lg"
                  )}
                >
                  <div className={cn(isGummy ? "font-black" : "font-bold")}>
                    <span>{status || "No Status"}</span>
                  </div>
                  <Badge
                    count={statusReminders.length}
                    action="info"
                    className={cn(
                      "mobile-pref-detail-badge static shrink-0",
                      isGummy && "h-10 min-w-10 px-3 text-base font-black"
                    )}
                  />
                </h3>
              </AccordionV2.Trigger>
            </div>
            <AccordionV2.Content>
              <ul
                className={cn(
                  "mt-4 grid grid-cols-1 sm:grid-cols-1",
                  isGummy
                    ? "gap-3"
                    : "divide-mono-200 dark:divide-mono-700/50 divide-y"
                )}
              >
                {statusReminders.map((reminderOriginal) => {
                  const reminder = structuredClone(
                    reminderOriginal
                  ) as typeof reminderOriginal & {
                    displayRecurringInterval: string;
                  };
                  reminder.displayRecurringInterval =
                    reminder.recurring_interval;
                  if (reminder.recurring_interval === "Never") {
                    reminder.displayRecurringInterval = "One time reminder";
                  }
                  const formattedDate = DateTime.fromISO(
                    reminder.alert_date ?? "",
                    {
                      zone: timezone
                    }
                  ).toFormat(`MMMM d, yyyy '@' h:mm a`);
                  return (
                    <li
                      key={reminder?.entity_name ?? "" + reminder.description}
                      className={cn(
                        "relative flex",
                        isGummy
                          ? cn(
                              "rounded-surface-md bg-white dark:bg-mono-900 px-5 shadow-card text-mono-800 dark:text-mono-100"
                            )
                          : "border-mono-300 dark:border-mono-700/50 hover:border-mono-400 text-mono-700 dark:text-mono-300"
                      )}
                    >
                      <div
                        className={cn(
                          "flex min-w-0 flex-1 flex-col gap-2 sm:flex-row md:gap-4",
                          isGummy ? "py-5" : "pb-4 pt-3"
                        )}
                      >
                        <div className="flex-1 focus:outline-none">
                          <h3
                            className={cn(
                              "mobile-pref-card-title",
                              isGummy
                                ? "text-lg font-black tracking-tight text-mono-900 dark:text-white"
                                : "text-base font-medium"
                            )}
                          >
                            {reminder.description}
                          </h3>
                          {reminder.alert_date && (
                            <h5
                              className={cn(
                                "mobile-pref-card-meta mb-1",
                                isGummy
                                  ? "mt-1 text-sm font-black uppercase tracking-widest text-mono-600 dark:text-mono-300"
                                  : "opacity-60"
                              )}
                            >
                              {formattedDate}
                            </h5>
                          )}
                          {subItems.reduce((acc, item) => {
                            if (reminder[item.field]) {
                              acc.push(
                                <p
                                  key={item.field}
                                  className={cn(
                                    "mobile-pref-card-body flex items-center gap-3 pt-2 leading-tight",
                                    isGummy
                                      ? "text-base font-bold text-mono-700 dark:text-mono-200"
                                      : "text-sm opacity-60"
                                  )}
                                >
                                  <i
                                    className={`mobile-pref-view-inline-icon fa-regular ${decodeFA(
                                      item.icon
                                    )}`}
                                  />
                                  <span>{reminder[item.field]}</span>
                                </p>
                              );
                            }
                            return acc;
                          }, [] as React.JSX.Element[])}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </AccordionV2.Content>
          </AccordionV2.Item>
        ))}
      </AccordionV2.PersistentRoot>
    </div>
  );
};

const ViewRemindersSkeleton = () => {
  return (
    <div className="h-full w-full animate-pulse">
      <div className="bg-mono-200 dark:bg-mono-900 h-10 w-full rounded-md" />
    </div>
  );
};

ViewReminders.Provider = ViewRemindersProviderWrapper;
ViewReminders.useViewReminders = useViewReminders;
ViewReminders.Skeleton = ViewRemindersSkeleton;
ViewReminders.displayName = "ViewReminders";
export default ViewReminders;
